import { AccessProvider } from '@/contexts/AccessContext';
import AppLayout from '@/layouts/AppLayout';
import { createBrowserRouter, Navigate, redirect, type RouteObject } from 'react-router-dom';
import Login from '@/pages/Login';
import {
  verifyAuthToken,
  verifyRoleInvitationToken,
  validateExternalAuditorSetupToken,
} from '@/services/Login/auth';
import { isAuthenticated } from '@/services/Login/auth';
import LoginPage from '@/components/LoginForm';
import RouteErrorBoundary from '@/components/RouteErrorBoundary';
import RouteHydrateFallback from '@/components/RouteHydrateFallback';

// React Router resolves every matched route's `lazy()` module in parallel during navigation
// (parent and child together, not sequentially), and dedupes repeat import() calls against the
// same chunk — so a page used by more than one route (e.g. ComingSoon) only ships once.
const page = (
  loader: () => Promise<{ default: RouteObject['Component'] }>
): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await loader()).default }),
});

const appRoutes: RouteObject[] = [
  {
    path: '/external-auditor/*',
    ...page(() => import('@/features/ExternalAuditor/ExternalAuditor')),
    loader: async ({ request }) => {
      const token = new URL(request.url).searchParams.get('token');
      if (!token) return { valid: null };
      try {
        const res = await validateExternalAuditorSetupToken(token);
        if (res.data.status === 'valid') {
          return {
            valid: true,
            inviteeName: res.data.inviteeName || '',
            inviteeEmail: res.data.inviteeEmail || '',
            token,
          };
        }
        return { valid: false, status: res.data?.status || 'invalid', token };
      } catch (error) {
        const err = error as { response?: { data?: { status?: string } } };
        return { valid: false, status: err?.response?.data?.status || 'invalid', token };
      }
    },
  },
  { path: '/login', element: <Login /> },
  { path: '/test-login', element: <LoginPage /> },
  {
    path: '/verify',
    loader: async ({ request }) => {
      try {
        const authToken = new URL(request.url).searchParams.get('token');
        if (!authToken) throw redirect('/login');
        const response = await verifyAuthToken(authToken);
        if (response.statusCode === 200) throw redirect('/dashboard');
        throw redirect('/login');
      } catch (error) {
        if (error instanceof Response) throw error;
        throw redirect('/login');
      }
    },
  },
  {
    path: '/role-invitation',
    loader: async ({ request }) => {
      try {
        const token = new URL(request.url).searchParams.get('token');
        const action = new URL(request.url).searchParams.get('action');
        if (!token) throw redirect('/login');
        if (!action) throw redirect('/login');
        const response = await verifyRoleInvitationToken(token, action);
        if (response.statusCode === 200) throw redirect('/dashboard');
        throw redirect('/login');
      } catch (error) {
        if (error instanceof Response) throw error;
        const message =
          error instanceof Error
            ? error.message
            : 'This invitation link is invalid or has expired.';
        throw redirect(`/login?invitationError=${encodeURIComponent(message)}`);
      }
    },
  },
  {
    path: '',
    loader: isAuthenticated,
    element: (
      <AccessProvider>
        <AppLayout />
      </AccessProvider>
    ),
    children: [
      {
        // Pathless wrapper so a page error (including a failed chunk load) only replaces the
        // <Outlet /> content — AppLayout's sidebar/header stay mounted and usable.
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: '/dashboard', element: <></> },
          {
            path: 'documents/:id',
            ...page(
              () => import('@/features/Docs&Policies/components/DocumentDetails/DocumentDetails')
            ),
          },
          {
            path: 'documents',
            ...page(() => import('@/features/Docs&Policies/DocumentsAndPolicies')),
          },
          {
            path: 'user-settings',
            ...page(() => import('@/features/UserManagement/UserManagement')),
          },
          {
            path: 'compliance-controls',
            ...page(() => import('@/features/ComplianceControls/ComplianceControls')),
            children: [
              { index: true, element: <Navigate to="control-register" replace /> },
              {
                path: 'control-register',
                ...page(
                  () =>
                    import('@/features/ComplianceControls/components/ControlRegister/ControlRegister')
                ),
              },
              {
                path: 'cross-framework-mapping',
                ...page(
                  () =>
                    import('@/features/ComplianceControls/components/CrossFrameworkMapping/CrossFrameworkMapping')
                ),
              },
              {
                path: 'sop-mapping',
                ...page(
                  () => import('@/features/ComplianceControls/components/SOPMapping/SOPMapping')
                ),
              },
              {
                path: 'gap-analysis',
                ...page(
                  () => import('@/features/ComplianceControls/components/GapAnalysis/GapAnalysis')
                ),
              },
              { path: 'control-testing', element: <div>Control Testing Content</div> },
              {
                path: 'statement-of-applicability',
                ...page(
                  () =>
                    import('@/features/ComplianceControls/components/StatementOfApplicabilty/StatementApplicabilty')
                ),
              },
              { path: 'compliance-calendar', element: <div>Compliance Calendar Content</div> },
            ],
          },
          {
            path: 'compliance-controls/:controlId',
            lazy: async () => ({
              Component: (
                await import('@/features/ComplianceControls/components/ControlDetailsPage/ControlDetailPage')
              ).ControlDetailPage,
            }),
          },
          {
            path: 'audit-management',
            ...page(() => import('@/features/AuditManagement/AuditManagement')),
            children: [
              { index: true, element: <Navigate to="audit-registry" replace /> },
              {
                path: 'audit-registry',
                ...page(
                  () => import('@/features/AuditManagement/components/AuditRegistry/AuditRegistry')
                ),
              },
              {
                path: 'my-actions',
                ...page(() => import('@/features/AuditManagement/components/MyActions/MyActions')),
              },
              {
                path: 'finding-register',
                ...page(
                  () =>
                    import('@/features/AuditManagement/components/FindingRegister/FindingRegister')
                ),
              },
              {
                path: 'audit-reports',
                ...page(
                  () => import('@/features/AuditManagement/components/AuditReports/AuditReports')
                ),
              },
              {
                path: 'mrm',
                ...page(() => import('@/features/AuditManagement/components/Mrm/Mrm')),
              },
            ],
          },
          {
            path: 'audit-management/:auditId',
            ...page(
              () => import('@/features/AuditManagement/components/AuditDetails/AuditDetails')
            ),
          },
          {
            path: 'risk-management',
            ...page(() => import('@/features/RiskManagement/RiskManagement')),
            children: [
              { index: true, element: <Navigate to="risk-register" replace /> },
              {
                path: 'risk-register',
                ...page(
                  () => import('@/features/RiskManagement/components/RiskRegister/RiskRegister')
                ),
              },
              {
                path: 'mitigation-plans',
                ...page(
                  () =>
                    import('@/features/RiskManagement/components/MitigationPlans/MitigationPlans')
                ),
              },
              {
                path: 'review-cycle',
                ...page(
                  () => import('@/features/RiskManagement/components/ReviewCycle/ReviewCycle')
                ),
              },
              {
                path: 'risk-appetite',
                ...page(
                  () => import('@/features/RiskManagement/components/RiskAppetite/RiskAppetite')
                ),
              },
            ],
          },
          {
            path: 'risk-management/:riskId',
            ...page(() => import('@/features/RiskManagement/components/RiskDetails/RiskDetails')),
          },
          {
            path: 'evidence-management',
            ...page(() => import('@/features/EvidenceManagement/EvidenceManagement')),
            children: [
              {
                index: true,
                ...page(
                  () =>
                    import('@/features/EvidenceManagement/components/EvidenceManagementIndexRedirect')
                ),
              },
              {
                path: 'evidence-repository',
                ...page(
                  () =>
                    import('@/features/EvidenceManagement/components/EvidenceRepository/EvidenceRepository')
                ),
              },
              {
                path: 'audit-evidence-pack',
                ...page(
                  () =>
                    import('@/features/EvidenceManagement/components/AuditEvidencePack/AuditEvidencePack')
                ),
              },
              {
                path: 'validity-and-expiry',
                ...page(
                  () =>
                    import('@/features/EvidenceManagement/components/ValidityAndExpiry/ValidityAndExpiry')
                ),
              },
            ],
          },
          {
            path: 'evidence-management/:evidenceId',
            ...page(
              () =>
                import('@/features/EvidenceManagement/components/EvidenceDetails/EvidenceDetails')
            ),
          },
        ],
      },
    ],
  },
];

// Single pathless parent so every current and future top-level route inherits the same
// HydrateFallback (React Router uses the nearest parent's) and error boundary — a new loader or
// lazy route can't regress to a blank screen on cold load by forgetting to set its own.
export const AppRoutes = createBrowserRouter([
  {
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: RouteHydrateFallback,
    children: appRoutes,
  },
]);
